import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, UserCheck, Clock, ArrowLeft, UserPlus, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { OnlineDot } from "@/components/OnlineDot";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { StaffBadge } from "@/components/StaffBadge";

interface DiscoverUser {
  id: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  grade?: number;
  school?: string;
  currentLevel?: number;
}

interface FollowUser {
  followId: string;
  followerId?: string;
  followingId?: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  grade?: number;
  school?: string;
  isOnline?: boolean;
  createdAt?: string;
  acceptedAt?: string;
  role?: string;
}

function FollowUserRow({
  user,
  actions,
}: {
  user: FollowUser;
  actions: React.ReactNode;
}) {
  const [, navigate] = useLocation();
  const userId = user.followerId ?? user.followingId ?? null;

  return (
    <div className="flex items-center gap-3 py-3">
      <div
        className="relative cursor-pointer flex-shrink-0"
        onClick={() => userId && navigate(`/user/${userId}`)}
      >
        <Avatar className="h-10 w-10">
          <AvatarImage src={user.profilePhotoUrl || ""} />
          <AvatarFallback className="bg-primary/10 text-sm">
            {user.firstName?.[0]}{user.lastName?.[0]}
          </AvatarFallback>
        </Avatar>
        <span className="absolute -bottom-0.5 -right-0.5">
          <OnlineDot isOnline={user.isOnline ?? false} className="w-3 h-3 border-2 border-background" />
        </span>
      </div>
      <div
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => userId && navigate(`/user/${userId}`)}
      >
        <p className="font-medium text-sm truncate flex items-center gap-1">
          {user.firstName} {user.lastName}
          {user.role === 'admin' && <StaffBadge />}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {user.school ? `${user.school} · ` : ""}Grade {user.grade}
        </p>
      </div>
      <div className="flex-shrink-0">{actions}</div>
    </div>
  );
}

export default function FollowsPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("discover");
  const [search, setSearch] = useState("");
  const [sentRequests, setSentRequests] = useState<Set<string>>(new Set());

  const { data: discoverUsers = [] } = useQuery<DiscoverUser[]>({
    queryKey: ["/api/social/discover", search],
    queryFn: async () => {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await apiRequest("GET", `/api/social/discover${params}`);
      return res.json();
    },
  });

  const { data: followers = [] } = useQuery<FollowUser[]>({
    queryKey: ["/api/social/followers"],
  });
  const { data: following = [] } = useQuery<FollowUser[]>({
    queryKey: ["/api/social/following"],
  });
  const { data: pending = [] } = useQuery<FollowUser[]>({
    queryKey: ["/api/social/pending"],
  });

  // Build userIds arrays for online status
  const followerIds = followers.map(u => u.followerId ?? "").filter(Boolean);
  const followingIds = following.map(u => u.followingId ?? "").filter(Boolean);
  const pendingIds = pending.map(u => u.followerId ?? "").filter(Boolean);
  const allIds = [...new Set([...followerIds, ...followingIds, ...pendingIds])];
  const { isOnline } = useOnlineStatus(allIds);

  const followMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      await apiRequest("POST", `/api/social/follow/${targetUserId}`);
    },
    onSuccess: (_data, targetUserId) => {
      setSentRequests(prev => new Set([...prev, targetUserId]));
      queryClient.invalidateQueries({ queryKey: ["/api/social/discover"] });
      toast({ title: "Follow request sent" });
    },
    onError: () => {
      toast({ title: "Failed to send follow request", variant: "destructive" });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      await apiRequest("DELETE", `/api/social/follow/${targetUserId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/following"] });
      toast({ title: "Unfollowed" });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async (followId: string) => {
      await apiRequest("POST", `/api/social/follow/${followId}/accept`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/social/followers"] });
      toast({ title: "Follow request accepted" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (followId: string) => {
      await apiRequest("POST", `/api/social/follow/${followId}/reject`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/pending"] });
      toast({ title: "Follow request declined" });
    },
  });

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <Button
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>

      <h1 className="text-2xl font-bold mb-6">Social</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="discover" className="flex items-center gap-1">
            <Search className="h-3.5 w-3.5 hidden sm:block" />
            Find
          </TabsTrigger>
          <TabsTrigger value="followers" className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5 hidden sm:block" />
            <span className="hidden xs:inline">Followers</span>
            <span className="xs:hidden">Fans</span>
            {followers.length > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-1 py-0.5 rounded-full leading-none">
                {followers.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="following" className="flex items-center gap-1">
            <UserCheck className="h-3.5 w-3.5 hidden sm:block" />
            Following
            {following.length > 0 && (
              <span className="text-xs bg-primary/10 text-primary px-1 py-0.5 rounded-full leading-none">
                {following.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 hidden sm:block" />
            Requests
            {pending.length > 0 && (
              <span className="text-xs bg-orange-500 text-white px-1 py-0.5 rounded-full leading-none">
                {pending.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="discover">
          <div className="mb-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or school..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Card>
            <CardContent className="p-4 divide-y divide-border">
              {discoverUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {search ? "No students found." : "You're following everyone in your grade!"}
                </p>
              ) : (
                discoverUsers.map(u => (
                  <div key={u.id} className="flex items-center gap-3 py-3">
                    <div
                      className="relative cursor-pointer flex-shrink-0"
                      onClick={() => navigate(`/user/${u.id}`)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={u.profilePhotoUrl || ""} />
                        <AvatarFallback className="bg-primary/10 text-sm">
                          {u.firstName?.[0]}{u.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(`/user/${u.id}`)}
                    >
                      <p className="font-medium text-sm truncate">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {u.school ? `${u.school} · ` : ""}Grade {u.grade} · Level {u.currentLevel}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant={sentRequests.has(u.id) ? "outline" : "default"}
                        onClick={() => !sentRequests.has(u.id) && followMutation.mutate(u.id)}
                        disabled={sentRequests.has(u.id) || followMutation.isPending}
                      >
                        <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                        {sentRequests.has(u.id) ? "Requested" : "Follow"}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="followers">
          <Card>
            <CardContent className="p-4 divide-y divide-border">
              {followers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No followers yet. Share your profile to get started!
                </p>
              ) : (
                followers.map(u => (
                  <FollowUserRow
                    key={u.followId}
                    user={{ ...u, isOnline: isOnline(u.followerId ?? "") }}
                    actions={null}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="following">
          <Card>
            <CardContent className="p-4 divide-y divide-border">
              {following.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  You're not following anyone yet. Visit someone's profile to follow them.
                </p>
              ) : (
                following.map(u => (
                  <FollowUserRow
                    key={u.followId}
                    user={{ ...u, isOnline: isOnline(u.followingId ?? "") }}
                    actions={
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unfollowMutation.mutate(u.followingId ?? "")}
                        disabled={unfollowMutation.isPending}
                      >
                        Unfollow
                      </Button>
                    }
                  />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <CardContent className="p-4 divide-y divide-border">
              {pending.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No pending follow requests.
                </p>
              ) : (
                pending.map(u => (
                  <FollowUserRow
                    key={u.followId}
                    user={{ ...u, isOnline: isOnline(u.followerId ?? "") }}
                    actions={
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => acceptMutation.mutate(u.followId)}
                          disabled={acceptMutation.isPending}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rejectMutation.mutate(u.followId)}
                          disabled={rejectMutation.isPending}
                        >
                          Decline
                        </Button>
                      </div>
                    }
                  />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
